import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type {
  DrivingTestsTotalMsDataItem,
  DrivingTestsTotalReDataItem,
} from 'teacher-ui-client/components/giraffe/charts/driving-tests/totals'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface DrivingTestsTotalsResponseRe {
  driving_tests_totals_summary_by_grade: ReSummaryData[]
  driving_tests_totals_by_grade: ReGradeData[]
}

interface ReSummaryData {
  driving_test_grade: null
  data: [
    { category: 'completed_count'; count: number },
    { category: 'student_count'; count: number },
    { category: 'student_average'; average: Nullable<number> },
  ]
}

interface ReGradeData {
  driving_test_grade: null
  categories: {
    category: 'letters_and_sounds' | 'content_words' | 'sight_words'
    completed: number
    total_attempts: number
    percent_complete: number
  }[]
}

export async function drivingTestsTotalsRe(
  this: StudentEventsService,
  product: LegacyProductRe,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<DrivingTestsTotalReDataItem>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('driving_tests_totals_by_grade', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<DrivingTestsTotalsResponseRe>(url, query)
  if (!data) return null

  const { driving_tests_totals_summary_by_grade: summaryData, driving_tests_totals_by_grade: gradeData } = data
  if (summaryData.length === 0 || gradeData.length === 0) return null

  const completed_count = summaryData[0].data.find((summaryItem) => summaryItem.category === 'completed_count')!.count
  const student_count = summaryData[0].data.find((summaryItem) => summaryItem.category === 'student_count')!.count
  const student_average = summaryData[0].data.find((summaryItem) => summaryItem.category === 'student_average')!.average

  return {
    summary: {
      completed_count,
      student_count,
      student_average,
    },
    categories: gradeData[0].categories,
  }
}

export interface DrivingTestsTotalsResponseMs {
  driving_tests_totals_summary_by_grade: MsSummaryData[]
  driving_tests_totals_by_grade: MsGradeData[]
}

interface MsSummaryData {
  driving_test_grade: number
  data: [
    { category: 'completed_count'; count: number },
    { category: 'student_count'; count: number },
    { category: 'student_average'; average: Nullable<number> },
  ]
}

interface MsGradeData {
  driving_test_grade: number
  categories: {
    category: 'geometry' | 'measurement' | 'number' | 'operations' | 'patterns' | 'data'
    completed: number
    total_attempts: number
    percent_complete: number
  }[]
}

export async function drivingTestsTotalsMs(
  this: StudentEventsService,
  product: LegacyProductMs,
  statsScope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<DrivingTestsTotalMsDataItem[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('driving_tests_totals_by_grade', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<DrivingTestsTotalsResponseMs>(url, query)
  if (!data) return null

  const { driving_tests_totals_summary_by_grade: summaryData, driving_tests_totals_by_grade: gradeData } = data
  if (summaryData.length === 0 || gradeData.length === 0) return null

  return groupByGrade(summaryData, gradeData)
}

function groupByGrade(summaryData: MsSummaryData[], gradeData: MsGradeData[]) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.driving_test_grade - b.driving_test_grade)
  const sortedGradeData = [...gradeData].sort((a, b) => a.driving_test_grade - b.driving_test_grade)

  return sortedSummaryData.map((summary, index) => {
    const completed_count = summary.data.find((summaryItem) => summaryItem.category === 'completed_count')!.count
    const student_count = summary.data.find((summaryItem) => summaryItem.category === 'student_count')!.count
    const student_average = summary.data.find((summaryItem) => summaryItem.category === 'student_average')!.average

    return {
      driving_test_grade: summary.driving_test_grade,
      summary: {
        completed_count,
        student_count,
        student_average,
      },
      categories: sortedGradeData[index].categories,
    }
  })
}
